import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useCallback, useState } from 'react';
import ConsoleCard from './ConsoleCard';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { selectCurrentTaskAIStatus } from '../../redux/features/taskRouter/taskRouter.selector';
import {
  selectEventAPI,
  selectEventAPIAuth,
} from '../../redux/features/config/config.selector';
import { selectActiveRestaurantCode } from '../../selectors/restaurant';
import { selectUserProfile } from '../../selectors/user';
import { UserDetails } from '../../types';
import { TextFrameTransmissionMessage } from '../../reducers/messagingSlice';
import Colors from '../../utils/color';
import { HITL3_PADDING } from '../../utils/hitl3Spacing';
import { HITL3_FONT_SIZE } from '../../utils/hitl3Typography';
import ConfirmDialog from '../common/ConfirmDialog';
import { MessageContainer } from '../Chat/MessageContainer';
import useTranscriptMessages from '../TranscriptSliderArea/useTranscriptMessages.hooks';
import { sendAsrIncorrectEvent } from '../../utils/asrEvents';
import useIssueReport from '../../hooks/issueReport/useIssueReport.hooks';
import { ASR_MISSING_ISSUE_TYPE } from '../../constants/issueTypes';
import useIntervention from '../../hooks/intervention/useIntervention.hooks';
import useStickyScroll from '../../hooks/useStickyScroll.hooks';

// Stable empty list so blanking the transcript during staff intervention doesn't
// hand useStickyScroll a fresh array identity on every render.
const NO_MESSAGES: TextFrameTransmissionMessage[] = [];

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  // Transcript section: heading + inner white card. The grey card backdrop is
  // the shared left-pane card (MainPageLayout), so this stays transparent. The
  // takeover expansion is applied to that card (not this section), so the
  // reminder + AI status sections stay visible alongside the transcript.
  section: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    // Anchor for the "New messages" pill, which floats over the message stream.
    position: 'relative',
  },
  // The transcript message stream, below the header divider (supplied by
  // ConsoleCard). The body just adds the matching top breathing room below that
  // line, and horizontal room for the bubbles' tails.
  messages: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    // Horizontal room for the bubbles' protruding WhatsApp tails (they extend
    // ~6px past the bubble edge); overflowX hidden so that never shows an
    // x-scrollbar. Clipping is at the padding box, so the tails stay visible.
    overflowX: 'hidden',
    padding: HITL3_PADDING,
  },
  dividerContainer: {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: spacing(1),
  },
  divider: {
    fontSize: '14px',
    lineHeight: '20px',
    color: Colors.guardsmanRed,
  },
  underline: {
    flex: 1,
    padding: '0.75px',
    backgroundColor: Colors.guardsmanRed,
    margin: '5px',
    display: 'inline-block',
  },
  // Shown only while the agent has scrolled away from the live edge, so they
  // know updates are still arriving; clicking it returns them to following.
  newMessagesPill: {
    position: 'absolute',
    bottom: HITL3_PADDING,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 3,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    border: 'none',
    borderRadius: '999px',
    padding: `4px ${HITL3_PADDING}`,
    cursor: 'pointer',
    backgroundColor: Colors.readBack,
    color: Colors.white,
    fontSize: HITL3_FONT_SIZE,
    fontWeight: 600,
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.25)',
  },
  newMessagesIcon: {
    fontSize: '1rem',
  },
}));

/**
 * LIVE TRANSCRIPT (left pane). A single card: header (chat icon + label +
 * accordion chevron) above an inset divider, then the AI/customer message
 * stream (supervised-agent divider + click-to-report ASR-missing flow).
 * Collapsing via the chevron hides the stream, leaving just the header.
 *
 * NOTE: the volume control has been moved out of here for now.
 */
interface LiveTranscriptCardProps {
  // Accordion collapse, mirroring Customer Pulse / Action Center. Controlled
  // (open + onOpenChange) so the parent can sync the resizable panel; falls back
  // to internal state (default open) when used standalone.
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const LiveTranscriptCard = ({
  open: openProp,
  onOpenChange,
}: LiveTranscriptCardProps) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const currentTaskAIStatus = useAppSelector(selectCurrentTaskAIStatus);

  const [openState, setOpenState] = useState(true);
  const open = openProp ?? openState;
  const toggleOpen = () => {
    const next = !open;
    if (openProp === undefined) setOpenState(next);
    onOpenChange?.(next);
  };

  const eventAPI = useAppSelector(selectEventAPI);
  const eventAPIAuth = useAppSelector(selectEventAPIAuth);
  const restaurantCode = useAppSelector(selectActiveRestaurantCode);
  const userDetails = useAppSelector(selectUserProfile) as UserDetails | null;
  const effectiveSessionId = useAppSelector(
    (state) =>
      state.taskRouter.taskAssignedSessionId ||
      state.sessionBoundary.sessionId ||
      state.sessionBoundary.endedSessionId ||
      state.messages.currentSessionId
  );

  const { messagesList, sessionBufferMessagesList } = useTranscriptMessages();
  const { isStaffInterventionHappening } = useIntervention();
  // Staff intervention hands the conversation to a human, so the AI transcript is
  // no longer the live record — blank it out. This is a view-layer clear only:
  // the underlying frames stay in Redux, so if the intervention resolves the
  // stream reappears intact (no data destroyed).
  const displayedMessages = isStaffInterventionHappening
    ? NO_MESSAGES
    : messagesList;
  const displayedSessionBuffer = isStaffInterventionHappening
    ? NO_MESSAGES
    : sessionBufferMessagesList;
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  /**
   * HITL-1501: with issue reporting on, a blank-area click files a real issue report
   * instead of the legacy `asr_incorrect` event, and the confirm dialog goes with
   * it — the flag panel IS the confirmation, and it carries Undo.
   *
   * The legacy path is GATED, not deleted. The flag is per-store, so deleting it
   * would strip ASR reporting from every store that does not have the new feature
   * on. With the flag off everything below behaves exactly as it did.
   */
  const { isEnabled: isIssueReportingEnabled, flagIssue } = useIssueReport();

  // HITL-1481: the scroll container is this card's message stream, so follow-mode
  // is owned here rather than inside MessageContainer. `open` joins the session id
  // in the reset key because collapsing hides the stream with `display: none`,
  // which drops its scroll offset — there is nothing left to preserve, so a
  // re-expand lands back at the live edge.
  const { scrollRef, handleScroll, isPinned, hasNewMessages, scrollToBottom } =
    useStickyScroll(
      [displayedMessages, displayedSessionBuffer],
      `${effectiveSessionId}:${open}`
    );

  const handleTranscriptAreaClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      // During staff intervention the transcript is blanked, so there is no word
      // to report as ASR-incorrect — don't surface the "ASR MISSING" confirm.
      if (isStaffInterventionHappening) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (
        typeof document !== 'undefined' &&
        document.querySelector('[role="dialog"]')
      ) {
        return;
      }
      if (
        target &&
        (target.closest('[data-word]') || target.closest('[role="dialog"]'))
      ) {
        return;
      }
      if (isIssueReportingEnabled) {
        // PILOT DUAL-EMIT — same reasoning as the word-click path in
        // TranscriptMessage: the legacy `asr_incorrect` series stays continuous for
        // pilot stores instead of going silent under a different event type.
        sendAsrIncorrectEvent({
          eventAPI,
          eventAPIAuth,
          restaurantCode,
          effectiveSessionId,
          userDetails,
          wordClicked: 'ASR MISSING',
          dispatch,
        });
        // The gesture IS the classification — a click on the empty transcript means
        // "nothing was transcribed here" and nothing else — so the type rides on
        // revision 0 rather than waiting for the agent to pick it in the panel.
        //
        // TRADE-OFF, accepted deliberately: this used to be a two-step action behind
        // a dialog and is now one click on a large target, so a stray click files a
        // report. Undo covers it for the length of the flag window, and the one-open
        // -flag guard stops a run of them. Worth watching the pilot for a
        // Missing Transcription spike specifically.
        flagIssue({
          source: 'transcript_turn',
          issue_type: ASR_MISSING_ISSUE_TYPE,
        });
        return;
      }
      setConfirmDialogOpen(true);
    },
    [
      isStaffInterventionHappening,
      isIssueReportingEnabled,
      flagIssue,
      eventAPI,
      eventAPIAuth,
      restaurantCode,
      effectiveSessionId,
      userDetails,
      dispatch,
    ]
  );

  const handleConfirmBlankClick = useCallback(() => {
    sendAsrIncorrectEvent({
      eventAPI,
      eventAPIAuth,
      restaurantCode,
      effectiveSessionId,
      userDetails,
      wordClicked: 'ASR MISSING',
      dispatch,
    });
    setConfirmDialogOpen(false);
  }, [
    eventAPI,
    eventAPIAuth,
    restaurantCode,
    effectiveSessionId,
    userDetails,
    dispatch,
  ]);

  return (
    <div className={classes.section}>
      <ConsoleCard
        icon={<ChatBubbleOutlineOutlinedIcon />}
        title="Transcript"
        open={open}
        onToggle={toggleOpen}
        // Keep the message stream mounted (hidden) so collapse/expand is instant
        // and messages aren't re-rendered from scratch on every expand.
        keepBodyMounted
      >
        <div
          className={classes.messages}
          data-transcript="true"
          ref={scrollRef}
          onScroll={handleScroll}
          onClick={handleTranscriptAreaClick}
        >
          {displayedSessionBuffer.length > 0 && (
            <MessageContainer messageList={displayedSessionBuffer} compact />
          )}
          {currentTaskAIStatus && displayedSessionBuffer.length > 0 && (
            <div className={classes.dividerContainer}>
              <div className={classes.underline} />
              <div className={classes.divider}>With Supervised Agent</div>
              <div className={classes.underline} />
            </div>
          )}
          <MessageContainer messageList={displayedMessages} compact />
        </div>
      </ConsoleCard>
      {/*
        Deliberately rendered outside the scrollable div above: that div's onClick
        opens the "ASR MISSING" confirm, and the pill must never trigger it.

        data-transcript marks this as a read-back control rather than an order
        interaction, so the agent-interception guards skip it. They listen on
        document in the capture phase, so nothing on this element could stop them.
      */}
      {open && hasNewMessages && !isPinned && (
        <button
          type="button"
          data-testid="transcript-new-messages"
          data-transcript="true"
          className={classes.newMessagesPill}
          onClick={scrollToBottom}
        >
          <KeyboardArrowDownIcon className={classes.newMessagesIcon} />
          New messages
        </button>
      )}
      <ConfirmDialog
        // Portal-rendered, so it needs the transcript marker itself — confirming
        // an ASR report is still not taking over the order.
        data-transcript="true"
        open={
          confirmDialogOpen &&
          !isStaffInterventionHappening &&
          !isIssueReportingEnabled
        }
        content={'Send ASR incorrect event for word: "ASR MISSING"?'}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={handleConfirmBlankClick}
        closeBtnText="Cancel"
        confirmBtnText="OK"
      />
    </div>
  );
};

export default LiveTranscriptCard;
